import React, { useState, useCallback } from 'react';
import { Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoginLogQuery, LoginLogFilters } from './hooks/useLoginLogQuery';
import { LoginLogType } from './graphql/queries/getLoginLogs';
import LoginLogFilter from './components/LoginLogFilter';
import TablePaginationBar from '../../../components/TablePaginationBar';
import dayjs from 'dayjs';

const initialFilters: LoginLogFilters = {};

const LoginLogPage: React.FC = () => {
  const { t } = useTranslation();
  const [tempFilters, setTempFilters] = useState<LoginLogFilters>(initialFilters);
  const [filters, setFilters] = useState<LoginLogFilters>(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const { data, total, loading } = useLoginLogQuery(filters, currentPage, pageSize);

  const handleFilter = useCallback(() => {
    setFilters(tempFilters);
    setCurrentPage(1);
  }, [tempFilters]);

  const handleReset = useCallback(() => {
    setTempFilters(initialFilters);
    setFilters(initialFilters);
    setCurrentPage(1);
  }, []);

  const columns = [
    {
      title: t('supportTools.loginLog.columns.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: t('supportTools.loginLog.columns.accountId'),
      dataIndex: 'accountId',
      key: 'accountId',
      width: 100,
    },
    {
      title: t('supportTools.loginLog.columns.phone'),
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
    },
    {
      title: t('supportTools.loginLog.columns.operationType'),
      dataIndex: 'operationType',
      key: 'operationType',
      width: 130,
      render: (val: string) => t(`supportTools.loginLog.operationTypes.${val}`, val),
    },
    {
      title: t('supportTools.loginLog.columns.clientType'),
      dataIndex: 'clientType',
      key: 'clientType',
      width: 90,
    },
    {
      title: t('supportTools.loginLog.columns.clientIp'),
      dataIndex: 'clientIp',
      key: 'clientIp',
      width: 130,
    },
    {
      title: t('supportTools.loginLog.columns.loginResult'),
      dataIndex: 'loginResult',
      key: 'loginResult',
      width: 90,
      render: (val: string) => t(`supportTools.loginLog.results.${val}`, val),
    },
    {
      title: t('supportTools.loginLog.columns.failureReason'),
      dataIndex: 'failureReason',
      key: 'failureReason',
      width: 150,
      ellipsis: true,
      render: (val: string | null) => val ? t(`supportTools.loginLog.failureReasons.${val}`, val) : '-',
    },
    {
      title: t('supportTools.loginLog.columns.createdBy'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 100,
    },
    {
      title: t('supportTools.loginLog.columns.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (val: number | null) => val ? dayjs.unix(val).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
      <LoginLogFilter
        filters={tempFilters}
        onChange={setTempFilters}
        onFilter={handleFilter}
        onReset={handleReset}
      />
      <Table<LoginLogType>
        dataSource={data}
        columns={columns}
        loading={loading}
        pagination={false}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        size="middle"
      />
      <TablePaginationBar
        current={currentPage}
        pageSize={pageSize}
        total={total}
        onChange={(page, size) => {
          setCurrentPage(page);
          setPageSize(size);
        }}
      />
    </div>
  );
};

export default LoginLogPage;
